import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  CheckCircle,
  XCircle,
  RefreshCw,
  UserPlus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Wifi,
  WifiOff,
} from 'lucide-react';
import DataTable from '../common/DataTable';
import type { Column } from '../common/DataTable';
import StatCard from '../common/StatCard';
import ConfirmDialog from '../common/ConfirmDialog';
import { useToast } from '../common/ToastContainer';
import vpnUserService, { type VPNUser } from '../../services/vpnUser.service';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

interface VPNUsersTabProps {
  tenantId?: string;
  tenantName?: string;
  onInviteClick?: () => void;
  onCountChange?: (count: number) => void;
}

const VPNUsersTab = ({ tenantId, tenantName, onInviteClick, onCountChange }: VPNUsersTabProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<VPNUser[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });

  // Modal states
  const [deleteUser, setDeleteUser] = useState<VPNUser | undefined>();
  const [toggleUser, setToggleUser] = useState<VPNUser | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const result = await vpnUserService.list({
        tenantId,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setUsers(result.items);
      setPagination(result.pagination);
      onCountChange?.(result.pagination.total);
    } catch (error) {
      console.error('Failed to fetch VPN users:', error);
      toast.error('Load Failed', 'Failed to load VPN users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [tenantId, pagination.page]);

  const handleRefresh = () => {
    fetchUsers();
  };

  const handleDeleteClick = (user: VPNUser) => {
    setDeleteUser(user);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUser) return;

    try {
      setIsDeleting(true);
      await vpnUserService.delete(deleteUser.id);
      toast.success('User Deleted', `VPN user ${deleteUser.email} has been deleted`);
      await fetchUsers();
      setDeleteUser(undefined);
    } catch (error) {
      console.error('Failed to delete VPN user:', error);
      toast.error('Delete Failed', 'Failed to delete VPN user');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleClick = (user: VPNUser) => {
    setToggleUser(user);
  };

  const handleToggleConfirm = async () => {
    if (!toggleUser) return;

    try {
      setIsToggling(true);
      if (toggleUser.status === 0) {
        await vpnUserService.disable(toggleUser.id);
        toast.success('User Disabled', `VPN user ${toggleUser.email} has been disabled`);
      } else {
        await vpnUserService.enable(toggleUser.id);
        toast.success('User Enabled', `VPN user ${toggleUser.email} has been enabled`);
      }
      await fetchUsers();
      setToggleUser(undefined);
    } catch (error) {
      console.error('Failed to toggle VPN user:', error);
      toast.error('Action Failed', 'Failed to update VPN user status');
    } finally {
      setIsToggling(false);
    }
  };

  // Stats
  const stats = {
    total: pagination.total,
    active: users.filter((u) => u.status === 0).length,
    inactive: users.filter((u) => u.status !== 0).length,
    connected: users.filter((u) => u.lastConnection).length,
  };

  // Table columns
  const columns: Column<VPNUser>[] = [
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (u) => (
        <button
          onClick={() => navigate(`/admin/vpn-users/${u.id}`)}
          className="flex items-center space-x-2 text-left hover:text-primary-600 transition-colors"
        >
          <Users className="w-4 h-4 text-primary-600" />
          <span className="font-medium">{u.email}</span>
        </button>
      ),
    },
    {
      key: 'deviceGroupName',
      label: 'Device Group',
      render: (u) => {
        const names = u.deviceGroupNames || [];
        if (names.length === 0) {
          return <span className="text-sm text-gray-400">-</span>;
        }
        if (names.length === 1) {
          return <span className="text-sm">{names[0]}</span>;
        }
        return (
          <div className="flex items-center space-x-1">
            <span className="text-sm font-medium">{names[0]}</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
              +{names.length - 1} more
            </span>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (u) => (
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${u.status === 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'
            }`}
        >
          {u.status === 0 ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'lastConnection',
      label: 'Last Connection',
      render: (u) => (
        <div className="flex items-center space-x-1">
          {u.lastConnection ? (
            <>
              <Wifi className="w-3 h-3 text-success-500" />
              <span className="text-sm text-gray-600">
                {formatDate(u.lastConnection, useSettingsStore.getState().getDateFormatSettings())}
              </span>
            </>
          ) : (
            <>
              <WifiOff className="w-3 h-3 text-gray-400" />
              <span className="text-sm text-gray-400">Never</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'connectionCount',
      label: 'Connections',
      render: (u) => <span className="text-sm">{u.connectionCount}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (u) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleToggleClick(u)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title={u.status === 0 ? 'Disable User' : 'Enable User'}
          >
            {u.status === 0 ? (
              <ToggleRight className="w-4 h-4 text-success-600" />
            ) : (
              <ToggleLeft className="w-4 h-4 text-gray-400" />
            )}
          </button>
          <button
            onClick={() => handleDeleteClick(u)}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Delete User"
          >
            <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            VPN client users{tenantName ? ` for ${tenantName}` : ''}
          </p>
        </div>
        <div className="flex space-x-3">
          <button onClick={handleRefresh} className="btn-secondary inline-flex items-center space-x-2">
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          {onInviteClick && (
            <button onClick={onInviteClick} className="btn-primary inline-flex items-center space-x-2">
              <UserPlus className="w-4 h-4" />
              <span>Invite VPN Users</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Users} title="Total Users" value={stats.total} color="primary" />
        <StatCard icon={CheckCircle} title="Active" value={stats.active} color="success" />
        <StatCard icon={XCircle} title="Disabled" value={stats.inactive} color="secondary" />
        <StatCard icon={Wifi} title="Connected" value={stats.connected} color="info" />
      </div>

      {/* Table */}
      <div className="card">
        <DataTable
          columns={columns}
          data={users}
          searchPlaceholder="Search VPN users..."
          loading={loading}
        />
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteUser}
        onClose={() => setDeleteUser(undefined)}
        onConfirm={handleDeleteConfirm}
        title="Delete VPN User"
        message={`Are you sure you want to delete VPN user "${deleteUser?.email}"?\n\nThis action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Toggle Status Confirmation */}
      <ConfirmDialog
        isOpen={!!toggleUser}
        onClose={() => setToggleUser(undefined)}
        onConfirm={handleToggleConfirm}
        title={toggleUser?.status === 0 ? 'Disable VPN User' : 'Enable VPN User'}
        message={
          toggleUser?.status === 0
            ? `Are you sure you want to disable VPN user "${toggleUser?.email}"?\n\nThey will no longer be able to connect.`
            : `Are you sure you want to enable VPN user "${toggleUser?.email}"?\n\nThey will be able to connect again.`
        }
        confirmText={toggleUser?.status === 0 ? 'Disable' : 'Enable'}
        cancelText="Cancel"
        variant={toggleUser?.status === 0 ? 'warning' : 'info'}
        isLoading={isToggling}
      />
    </div>
  );
};

export default VPNUsersTab;
